
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Search, Settings, CheckCircle, Plus } from "lucide-react"
import { useState } from "react"
import Slack from "../../static/svgs/Slack"
import Google from "../../static/svgs/Google"
import Salesforce from "../../static/svgs/Salesforce"
import LinkedIn from "../../static/svgs/LinkedIn"
import Zoom from "../../static/svgs/Zoom"
import Teams from "../../static/svgs/Teams"

export function IntegrationsBoard() {
  const [searchQuery, setSearchQuery] = useState("")

  const integrations = [
    {
      name: "Slack",
      description: "Team communication and sentiment analysis from channels and DMs",
      category: "Communication",
      status: "connected",
      logo: <Slack />,
      dataPoints: "Messages, Reactions, Thread Activity",
      lastSync: "2 minutes ago",
      color: "bg-purple-100 text-purple-700 border-purple-200",
    },
    {
      name: "Gmail",
      description: "Email sentiment analysis and communication patterns",
      category: "Email",
      status: "connected",
      logo: <Google />,
      dataPoints: "Email Tone, Response Times, Thread Length",
      lastSync: "5 minutes ago",
      color: "bg-red-100 text-red-700 border-red-200",
    },
    {
      name: "Zoom",
      description: "Meeting sentiment analysis and participation metrics",
      category: "Video Conferencing",
      status: "connected",
      logo: <Zoom />,
      dataPoints: "Meeting Sentiment, Participation, Speaking Time",
      lastSync: "15 minutes ago",
      color: "bg-blue-100 text-blue-700 border-blue-200",
    },
    // {
    //   name: "Salesforce",
    //   description: "CRM data and customer interaction emotional insights",
    //   category: "CRM",
    //   status: "connected",
    //   logo: <Salesforce />,
    //   dataPoints: "Deal Sentiment, Client Interactions, Pipeline Health",
    //   lastSync: "1 hour ago",
    //   color: "bg-blue-100 text-blue-700 border-blue-200",
    // },
    // {
    //   name: "LinkedIn",
    //   description: "Professional network engagement and team connections",
    //   category: "Social",
    //   status: "connected",
    //   logo: <LinkedIn />,
    //   dataPoints: "Post Engagement, Network Growth, Professional Updates",
    //   lastSync: "30 minutes ago",
    //   color: "bg-blue-100 text-blue-700 border-blue-200",
    // },
    // {
    //   name: "Microsoft Teams",
    //   description: "Team collaboration and meeting insights",
    //   category: "Communication",
    //   status: "available",
    //   logo: <Teams />,
    //   dataPoints: "Chat Sentiment, Meeting Analytics, File Sharing",
    //   lastSync: null,
    //   color: "bg-gray-100 text-gray-700 border-gray-200",
    // },
    // {
    //   name: "Jira",
    //   description: "Project management and team workload analysis",
    //   category: "Project Management",
    //   status: "available",
    //   logo: "J",
    //   dataPoints: "Ticket Sentiment, Workload Stress, Sprint Health",
    //   lastSync: null,
    //   color: "bg-gray-100 text-gray-700 border-gray-200",
    // },
  ]

  const categories = ["All", "Communication", "Email", "CRM", "Social", "Video Conferencing", "Project Management"]
  const [selectedCategory, setSelectedCategory] = useState("All")

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch =
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === "All" || integration.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const connectedCount = integrations.filter((i) => i.status === "connected").length

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">
            Connect your tools to unlock deeper emotional intelligence insights across your organization
          </p>
        </div>

        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="font-medium">{connectedCount} Connected</span>
          </div>
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-muted-foreground" />
            <span>{integrations.length - connectedCount} Available</span>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="text-xs"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 rounded-lg ${integration.color} flex items-center justify-center text-xl border`}
                  >
                    {typeof integration.logo === 'string' ? (
                      <span className="text-lg font-semibold">{integration.logo}</span>
                    ) : (
                      <div className="w-8 h-8 flex items-center justify-center integration-logo">
                        {integration.logo}
                      </div>
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{integration.name}</CardTitle>
                    <Badge variant="secondary" className="text-xs mt-1">
                      {integration.category}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {integration.status === "connected" ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">{integration.description}</p>

              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Data Points</div>
                <p className="text-sm">{integration.dataPoints}</p>
              </div>

              {integration.status === "connected" && integration.lastSync && (
                <div className="text-xs text-muted-foreground">Last sync: {integration.lastSync}</div>
              )}

              <div className="pt-2">
                {integration.status === "connected" ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 bg-transparent">
                      <Settings className="h-4 w-4 mr-2" />
                      Configure
                    </Button>
                    <Button variant="outline" size="sm">
                      View Data
                    </Button>
                  </div>
                ) : (
                  <Button className="w-full" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Connect
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredIntegrations.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No integrations found matching your search.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
